import { Injectable } from "@angular/core";
import { ITimeEntry, TimeEntryResponse } from "./models/ITimeEntry";
import { Observable, of, Subject } from "rxjs";
import { HttpClient, HttpParams } from "@angular/common/http";
import { DateTime } from "luxon";
import { map, tap } from "rxjs/operators";
import { ISelectableTimeEntry } from "../admin/projects/move-time/models/ISelectableTime";

const timeEntryUrl = "/api/timeentry";

@Injectable({
  providedIn: "root",
})
export class TimetrackingService {
  private timeChangeSubject = new Subject<any>();
  timeChanges$ = this.timeChangeSubject.asObservable();

  timePeriodQueryStamp = 0;
  timeBuffer = 60000; // 60 seconds

  constructor(private http: HttpClient) {}

  // for all users
  getHoursInPeriod(start: Date, end: Date) {}
  getHoursForMonth(month: number, year?: number) {
    const currentYear = DateTime.local().year;
    const start = DateTime.fromObject({
      year: year || currentYear,
      month,
      day: 1,
    });
  }
  getHoursForWeek(week: number, year?: number) {}
  getHoursForDay(day: number, year?: number) {}
  saveTimeEntry(entry: ITimeEntry): Observable<any> {
    if (entry.workDay instanceof DateTime) {
      // passing just date string to avoid any timezone translation;
      entry.workDay = entry.workDay.toFormat("yyyy-MM-dd") as any;
    }
    if (!entry.id) {
      return this.http.post<any>(timeEntryUrl, entry).pipe(
        tap(() => {
          this.timeChangeSubject.next();
        })
      );
    }
    return this.http.put<any>(`${timeEntryUrl}/${entry.id}`, entry).pipe(
      tap(() => {
        this.timeChangeSubject.next();
      })
    );
  }
  deleteTimeEntry(entryId: number) {
    return this.http.delete<ITimeEntry>(`${timeEntryUrl}/${entryId}`).pipe(
      tap(() => {
        this.timeChangeSubject.next();
      })
    );
  }
  getHoursForProject(projectId: number): Observable<ITimeEntry[]> {
    return this.http.get<ITimeEntry[]>(`${timeEntryUrl}/project/${projectId}`);
  }
  getHoursForWorkOrder(workOrderId: number): Observable<ITimeEntry[]> {
    return this.http.get<ITimeEntry[]>(
      `${timeEntryUrl}/workorder/${workOrderId}`
    );
  }
  moveTimeEntries(timeEntries: ITimeEntry[], targetWorkOrderId: number): Observable<string[]> {
    const timeEntryIds = timeEntries.map((entry) => entry.id);
    return this.http
      .put<string[]>(`${timeEntryUrl}/move/workorder/${targetWorkOrderId}`, timeEntryIds)
      .pipe(
        tap(() => {
          this.timeChangeSubject.next();
        })
      );
  }
  splitTimeEntries(timeEntries: ISelectableTimeEntry[], targetWorkOrderId: number): Observable<string[]> {
    const timeEntryIds = timeEntries.map((entry) => entry.id);
    const timeToSplit = timeEntries.map((entry) => entry.newTime);
    return this.http
      .put<string[]>(`${timeEntryUrl}/split/workorder/${targetWorkOrderId}`, {timeEntryIds: timeEntryIds, newMinutes: timeToSplit })
      .pipe(
        tap(() => {
          this.timeChangeSubject.next();
        })
      );
  }
  removeTimeEntriesFromWorkOrder(timeEntries: ITimeEntry[]) {
    const timeEntryIds = timeEntries.map((entry) => entry.id);
    return this.http.put(`${timeEntryUrl}/remove/workorder`, timeEntryIds);
  }
  // =============================================================
  // =============================================================
  // for specific user
  getUserHoursInPeriod(
    userId: number,
    start?: DateTime,
    end?: DateTime
  ): Observable<ITimeEntry[]> {
    const params = new HttpParams()
      .set("start", start.toISODate())
      .set("end", end.toISODate());
    // request all the time entries for a given user in this time period
    // grouped by project > work date. ordered by date asc
    return this.http
      .get<TimeEntryResponse[]>(`${timeEntryUrl}/user/${userId}`, {
        params,
      })
      .pipe(
        map((timeEntries) => {
          return timeEntries.map((timeEntry) =>
            this.mapResponseToEntry(timeEntry)
          );
        })
      );
  }
  getUserHoursForProject(userId: number, projectId: number, workDay: DateTime) {
    const params = new HttpParams()
      .set("userid", userId.toString())
      .set("start", workDay.toISODate())
      .set("end", workDay.toISODate());
    return this.http.get<ITimeEntry[]>(`${timeEntryUrl}/project/${projectId}`, {
      params,
    });
  }
  getTimeEntry(entryId: number): Observable<ITimeEntry> {
    return this.http
      .get<TimeEntryResponse>(`${timeEntryUrl}/${entryId}`)
      .pipe(map(this.mapResponseToEntry));
  }
  /* *********************************
  ************************************
  Utility
  */
  sortByWorkDayThenEntryDateDesc(a: ITimeEntry, b: ITimeEntry) {
    if (a.workDay < b.workDay) {
      return 1;
    }
    if (a.workDay > b.workDay) {
      return -1;
    }
    if (a.entryDate < b.entryDate) {
      return 1;
    }
    if (a.entryDate > b.entryDate) {
      return -1;
    }
    return 0;
  }
  canAddOnDay(date: DateTime): boolean {
    const today = DateTime.local().startOf("day");
    //cant add to the future
    if (today.toMillis() < date.startOf("day").toMillis()) {
      return false;
    }
    return true;
  }
  canEditOnDay(date: DateTime): Observable<boolean> {
    return this.getTimePeriods().pipe(
      map((timePeriods) => {
        let allowed = true;
        timePeriods.forEach((period) => {
          // TODO if date is in a locked time period and there is no exception for user
          // allowed = false
          // break;
        });
        return allowed;
      })
    );
  }

  private getTimePeriods(): Observable<any[]> {
    const now = DateTime.local().toMillis();
    if (now > this.timeBuffer + this.timePeriodQueryStamp) {
      // TODO query time periods with exceptions for user
      this.timePeriodQueryStamp = now;
      return of([]);
    } else {
      return of([]);
    }
  }
  private mapResponseToEntry(data: TimeEntryResponse): ITimeEntry {
    const timeEntry: ITimeEntry = {
      ...data,
      entryDate: DateTime.fromISO(data.entryDate),
      workDay: DateTime.fromISO(data.workDay),
    };
    return timeEntry;
  }
}
